import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Chip,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ItemGroup } from '@/types/itemGroup';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@mui/icons-material';

// Mock data - replace with API call
const mockItemGroup: ItemGroup = {
  id: '1',
  code: 'MED',
  name: 'Medicines',
  itemType: 'Inventory',
  isActive: true,
  createdAt: '2023-01-15T10:30:00Z',
  updatedAt: '2023-01-15T10:30:00Z',
};

const ItemGroupView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [itemGroup, setItemGroup] = useState<ItemGroup | null>(null);

  useEffect(() => {
    const fetchItemGroup = async () => {
      setLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // In a real app, you would fetch the item group by ID
        setItemGroup(mockItemGroup); // Replace with API call
        
      } catch (error) {
        console.error('Error fetching item group:', error);
        // Handle error (e.g., show error message, redirect, etc.)
      } finally {
        setLoading(false);
      }
    };

    fetchItemGroup();
  }, [id]);

  const handleEdit = () => {
    navigate(`/settings/item-groups/${id}/edit`);
  };

  const handleBack = () => {
    navigate('/settings/item-groups');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!itemGroup) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Item group not found
        </Typography>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to List
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Item Group Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBack}
            >
              Back to List
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEdit}
            >
              Edit
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Code
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemGroup.code}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Status
            </Typography>
            <Box>
              <Chip
                icon={itemGroup.isActive ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                label={itemGroup.isActive ? 'Active' : 'Inactive'}
                color={itemGroup.isActive ? 'success' : 'error'}
                size="small"
                variant="outlined"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Name
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemGroup.name}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Item Type
            </Typography>
            <Box>
              <Chip 
                label={itemGroup.itemType} 
                size="small"
                variant="outlined"
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Created At
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemGroup.createdAt ? new Date(itemGroup.createdAt).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Last Updated
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemGroup.updatedAt ? new Date(itemGroup.updatedAt).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBack}
          >
            Back to List
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleEdit}
          >
            Edit
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ItemGroupView;
